import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

const TRANSFORM_PATHS_0 = [
  { path: 'connection.createdDate' },
  { path: 'connection.updatedDate' },
];
const TRANSFORM_PATHS_1 = [{ path: 'fieldMask' }];
const TRANSFORM_PATHS_2 = [
  { path: 'connections.updatedDate' },
  { path: 'connections.connection.createdDate' },
  { path: 'connections.connection.updatedDate' },
];

function resolveWixIamConnectionV1ConnectionsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/connections-service',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/connections-service',
        destPath: '',
      },
      {
        srcPath: '/iam/connection/v1/connections',
        destPath: '/v1/connections',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/connections-service',
        destPath: '',
      },
      {
        srcPath: '/iam/connection/v1/connections',
        destPath: '/v1/connections',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/connections-service',
        destPath: '',
      },
      {
        srcPath: '/iam/connection/v1/connections',
        destPath: '/v1/connections',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/connections-service',
        destPath: '',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/connections-service',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/connections-service',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/connections-service',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/iam/connection/v1/connections',
        destPath: '/v1/connections',
      },
      {
        srcPath: '/iam/connection/v1/bulk/connections',
        destPath: '/v1/bulk/connections',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/iam/connection/v1/connections',
        destPath: '/v1/connections',
      },
      {
        srcPath: '/iam/connection/v1/bulk/connections',
        destPath: '/v1/bulk/connections',
      },
    ],
    _: [
      {
        srcPath: '/iam/connection/v1/connections',
        destPath: '/v1/connections',
      },
      {
        srcPath: '/iam/connection/v1/bulk/connections',
        destPath: '/v1/bulk/connections',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_identity_connection';

/**
 * Creates an authentication connection.
 *
 * This defines and configures the connection but does not activate it.
 * To make the connection available for user authentication, call [Enable Connection](https://dev.wix.com/docs/api-reference/wix-backoffice/identity/connection-v1/enable-connection).
 */
export function createConnection(payload: object): RequestOptionsFactory<any> {
  function __createConnection({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: TRANSFORM_PATHS_0,
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.iam.connection.v1.connection',
      method: 'POST' as any,
      methodFqn: 'wix.iam.connection.v1.ConnectionsService.CreateConnection',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamConnectionV1ConnectionsServiceUrl({
        protoPath: '/v1/connections',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_0,
          },
        ]),
    };

    return metadata;
  }

  return __createConnection;
}

/**
 * Updates a connection's configuration.
 *
 * Each time the connection is updated, `revision` increments by 1. The current `revision` must be passed when updating the connection. This ensures you're working with the latest connection and prevents unintended overwrites.
 *
 * You can update connection settings whether the connection is enabled or disabled.
 * Changes take effect immediately for enabled connections.
 */
export function updateConnection(payload: object): RequestOptionsFactory<any> {
  function __updateConnection({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: TRANSFORM_PATHS_1,
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: TRANSFORM_PATHS_0,
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.iam.connection.v1.connection',
      method: 'PATCH' as any,
      methodFqn: 'wix.iam.connection.v1.ConnectionsService.UpdateConnection',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamConnectionV1ConnectionsServiceUrl({
        protoPath: '/v1/connections/{connection.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_0,
          },
        ]),
    };

    return metadata;
  }

  return __updateConnection;
}

/**
 * Permanently deletes a connection.
 *
 * This removes the connection configuration entirely. If the connection is currently enabled,
 * it's automatically disabled before deletion. This action can't be undone.
 */
export function deleteConnection(payload: object): RequestOptionsFactory<any> {
  function __deleteConnection({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.connection.v1.connection',
      method: 'DELETE' as any,
      methodFqn: 'wix.iam.connection.v1.ConnectionsService.DeleteConnection',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamConnectionV1ConnectionsServiceUrl({
        protoPath: '/v1/connections/{connectionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteConnection;
}

/**
 * Enables a connection for user authentication.
 *
 * This activates a previously created connection, making it available for users to sign in with.
 * Calling this method for a connection that's already enabled succeeds, and the connection stays enabled.
 * To deactivate the connection, call [Disable Connection](https://dev.wix.com/docs/api-reference/wix-backoffice/identity/connection-v1/disable-connection).
 *
 * > **Note:** The connection ID isn't validated. If no connection exists with the specified ID, the call still succeeds,
 * > but no sign-in option is added. To verify that the connection was enabled, call
 * > [List Enabled Connections Client Data](https://dev.wix.com/docs/api-reference/wix-backoffice/identity/connection-v1/list-enabled-connections-client-data)
 * > and check that the connection appears in the response.
 */
export function enableConnection(payload: object): RequestOptionsFactory<any> {
  function __enableConnection({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.connection.v1.connection',
      method: 'POST' as any,
      methodFqn: 'wix.iam.connection.v1.ConnectionsService.EnableConnection',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamConnectionV1ConnectionsServiceUrl({
        protoPath: '/v1/connections/enabled',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __enableConnection;
}

/**
 * Disables a connection from user authentication.
 *
 * This deactivates the connection without deleting its configuration.
 * Users can no longer sign in using this connection until it's re-enabled.
 * To reactivate the connection, call [Enable Connection](https://dev.wix.com/docs/api-reference/wix-backoffice/identity/connection-v1/enable-connection).
 */
export function disableConnection(payload: object): RequestOptionsFactory<any> {
  function __disableConnection({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.connection.v1.connection',
      method: 'DELETE' as any,
      methodFqn: 'wix.iam.connection.v1.ConnectionsService.DisableConnection',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamConnectionV1ConnectionsServiceUrl({
        protoPath: '/v1/connections/enabled/{connectionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __disableConnection;
}

/**
 * Retrieves a connection by ID.
 *
 * Returns the connection configuration regardless of whether it's enabled or disabled. To retrieve only enabled connections, call [Get Enabled Connection](https://dev.wix.com/docs/api-reference/wix-backoffice/identity/connection-v1/get-enabled-connection).
 */
export function getConnection(payload: object): RequestOptionsFactory<any> {
  function __getConnection({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.connection.v1.connection',
      method: 'GET' as any,
      methodFqn: 'wix.iam.connection.v1.ConnectionsService.GetConnection',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamConnectionV1ConnectionsServiceUrl({
        protoPath: '/v1/connections/{connectionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_0,
          },
        ]),
    };

    return metadata;
  }

  return __getConnection;
}

/**
 * Retrieves an enabled connection by ID.
 *
 * Returns the connection only if it's currently enabled. Returns an error if the connection
 * exists but is disabled.
 */
export function getEnabledConnection(
  payload: object
): RequestOptionsFactory<any> {
  function __getEnabledConnection({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.connection.v1.connection',
      method: 'GET' as any,
      methodFqn:
        'wix.iam.connection.v1.ConnectionsService.GetEnabledConnection',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamConnectionV1ConnectionsServiceUrl({
        protoPath: '/v1/connections/enabled/{connectionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_0,
          },
        ]),
    };

    return metadata;
  }

  return __getEnabledConnection;
}

/**
 * Lists all connections with their enabled status.
 *
 * Returns both enabled and disabled connections, each with an `enabled` flag indicating
 * whether the connection is currently active for user authentication. The response includes
 * the site's built-in connections, in addition to any connections you create.
 */
export function listConnections(payload: object): RequestOptionsFactory<any> {
  function __listConnections({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.connection.v1.connection',
      method: 'GET' as any,
      methodFqn: 'wix.iam.connection.v1.ConnectionsService.ListConnections',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamConnectionV1ConnectionsServiceUrl({
        protoPath: '/v1/connections',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_2,
          },
        ]),
    };

    return metadata;
  }

  return __listConnections;
}

/**
 * Lists enabled connections with client-safe data only.
 *
 * Returns only enabled connections with a filtered data structure suitable for client-side
 * consumption, excluding sensitive server-side configuration details. The response includes
 * the site's built-in connections, in addition to any connections you create.
 */
export function listEnabledConnectionsClientData(
  payload: object
): RequestOptionsFactory<any> {
  function __listEnabledConnectionsClientData({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.connection.v1.connection',
      method: 'GET' as any,
      methodFqn:
        'wix.iam.connection.v1.ConnectionsService.ListEnabledConnectionsClientData',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamConnectionV1ConnectionsServiceUrl({
        protoPath: '/v1/connections/enabled/client',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listEnabledConnectionsClientData;
}

/**
 * Enables multiple connections in a single request.
 *
 * Activates up to 100 connections for user authentication. Each connection must already
 * exist and be properly configured.
 * To enable a single connection, call [Enable Connection](https://dev.wix.com/docs/api-reference/wix-backoffice/identity/connection-v1/enable-connection).
 */
export function bulkEnableConnections(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkEnableConnections({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.connection.v1.connection',
      method: 'POST' as any,
      methodFqn:
        'wix.iam.connection.v1.ConnectionsService.BulkEnableConnections',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamConnectionV1ConnectionsServiceUrl({
        protoPath: '/v1/bulk/connections/enable',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkEnableConnections;
}

/**
 * Disables multiple connections in a single request.
 *
 * Deactivates up to 100 connections without deleting their configurations.
 * Users can no longer sign in using these connections until they're re-enabled.
 * To disable a single connection, call [Disable Connection](https://dev.wix.com/docs/api-reference/wix-backoffice/identity/connection-v1/disable-connection).
 */
export function bulkDisableConnections(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDisableConnections({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.connection.v1.connection',
      method: 'POST' as any,
      methodFqn:
        'wix.iam.connection.v1.ConnectionsService.BulkDisableConnections',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamConnectionV1ConnectionsServiceUrl({
        protoPath: '/v1/bulk/connections/disable',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDisableConnections;
}
